/* eslint-disable react-native/no-inline-styles */
import React from 'react';
import { View, Text, ScrollView } from 'react-native';
import i18n from 'i18n-js';

import { scale } from '@components/frame';

import Line from '@components/common/line';
import TouchableElement from '@components/common/touchableElement';
import ActivateDevice from '@components/settings/components/common/activateDevice';
import EditField from '@components/common/editField';

import Loading, { SmallLoading } from '@components/loading';

import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import { getBoxShadowStyle } from '@styles/shadow';
import Header from '@components/settings/v2/common/SettingsHeader';
import { SCREEN_BORDER_RADIUS, SCREEN_PADDING_HORIZONTAL, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { getStateParams } from '@helpers';
import { useManageDeviceDetails } from './hooks/useManageDeviceDetails';

const LineButton = ({ C, onPress, loading, text, color, testID }) => {
  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  return (
    <View testID={testID} style={[shadowStyles, { height: scale(44), marginTop: scale(30), borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
      <TouchableElement
        onPress={() => {
          if (loading) return;
          if (onPress) onPress();
        }}
      >
        {({ boxStyle, pressHoverColor }) => (
          <View
            style={[
              {
                backgroundColor: C(pressHoverColor('Background / Light / Field')),
                borderRadius: scale(SCREEN_BORDER_RADIUS),
                height: scale(44),
                width: '100%',
                justifyContent: 'center',
                alignContent: 'center',
              },
              boxStyle,
            ]}
          >
            {loading ? (
              <SmallLoading />
            ) : (
              <Text style={[{ color: C(pressHoverColor(color)), textAlign: 'center' }, font.Body]}>{i18n.t(text)}</Text>
            )}
          </View>
        )}
      </TouchableElement>
    </View>
  );
};

const DeviceDetails = (props) => {
  const { C, route } = props;
  const id = getStateParams(route).id;

  const {
    deviceData,
    device,
    deleting,
    changingAccess,
    changeDeviceAccess,
    triggerDelete,
    showEditDeviceName,
    toggleEditDeviceName,
    onSubmitEditName,
  } = useManageDeviceDetails(id);

  return [
    <View style={{ height: '100%', width: '100%' }} key="device-details">
      <ActivateDevice />
      <Header backBtnText="Devices" name={'DeviceDetails'} key="device-details-header" />
      <ScrollView style={{ width: '100%', flex: 1 }} contentContainerStyle={{ paddingBottom: scale(50) }}>
        <View style={{ paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL) }}>
          {deviceData.deviceId ? (
            <View testID={TEST_IDS.settingsScreen.deviceDetailsContent} style={{ width: '100%' }}>
              <View style={[getBoxShadowStyle({ type: 'Field', C }), { borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
                {Object.keys(device).map((item, index) => (
                  <Line
                    {...{
                      item: device[item],
                      index,
                      array: Object.keys(device),
                      C,
                      roundTop: index === 0,
                      roundBottom: Object.keys(device).length - 1 === index,
                    }}
                    key={item}
                  />
                ))}
              </View>
              <LineButton
                {...{
                  C,
                  onPress: changeDeviceAccess,
                  loading: changingAccess,
                  text: deviceData.blocked ? 'Unblock Device' : 'Block Device',
                  color: 'Link/Light/Primary',
                  testID: TEST_IDS.settingsScreen.deviceDetailsBlockButton,
                }}
              />
              <LineButton
                {...{
                  C,
                  onPress: triggerDelete,
                  loading: deleting,
                  text: 'Remove Device',
                  color: 'Chart/Light/Warning',
                  testID: TEST_IDS.settingsScreen.deviceDetailsDeleteButton,
                }}
              />
            </View>
          ) : (
            <Loading background={C('Background/Light/Detail')} />
          )}
        </View>
      </ScrollView>
    </View>,
    <EditField
      key="EditField"
      {...{
        C,
        showModal: showEditDeviceName,
        onClose: toggleEditDeviceName,
        header: 'Name',
        subHeader: `${i18n.t('Edit the name of')} ${deviceData.name}`,
        initData: { name: deviceData.name },
        onSubmit: onSubmitEditName,
        inputs: [{ key: 'name', name: 'Name', noArrow: true, lableWidth: scale(110) }],
      }}
    />,
  ];
};

export default ColorWrapper(DeviceDetails);
